import { Component, Element, Event, h, Host, Listen, Prop, State } from "@stencil/core";
import whatInput from "what-input";
export class CButton {
  constructor() {
    this.accessible = true;
    this.version = '3.0.0';
    /* Variants */
    this.primary = false;
    this.minimal = false;
    /* Modifiers */
    this.block = false;
    this.rounded = false;
    this.alternate = false;
    this.disabled = false;
    this.active = false;
    this.centeredContent = false;
    this.alert = false;
    this.error = false;
    this.keyboardEventsEnabled = true;
    /* Sizes */
    this.large = false;
    this.small = false;
    this.autoHeight = false;
    this.focusable = true;
    /* State */
    this.readonly = false;
    this.role = 'button';
    this.whatIntent = null;
  }
  componentWillLoad() {
    if (this.href) {
      this.role = 'link';
    }
    else if (this.specificRole) {
      this.role = this.specificRole;
    }
  }
  handleClick(e) {
    if (this.readonly || this.disabled) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
    if (this.href) {
      if (this.target === "_blank") {
        window.open(this.href);
      }
      else {
        window.location.href = this.href;
      }
    }
    this.buttonClicked.emit();
  }
  handleBlur() {
    this.whatIntent = null;
  }
  handleFocus() {
    this.whatIntent = whatInput.ask('intent');
  }
  handleKeydown(e) {
    if (this.keyboardEventsEnabled && !e.altKey && !e.ctrlKey && !e.shiftKey) {
      if (['Enter', 'Space'].includes(e.code)) {
        this.el.click();
      }
      if (['Enter', 'Space', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.code)) {
        e.preventDefault();
        this.buttonKeydown.emit(e);
      }
    }
    return;
  }
  render() {
    let classNames = "c-button";
    if (this.autoHeight) {
      classNames += ' auto-height';
    }
    return (h(Host, { class: classNames, "data-whatintent": this.whatIntent, role: this.role, "aria-disabled": this.disabled, tabindex: this.accessible ? 0 : -1, "aria-label": this.ariaLabel },
      h("slot", null)));
  }
  static get is() { return "c-button"; }
  static get encapsulation() { return "shadow"; }
  static get originalStyleUrls() { return {
    "$": ["c-button.scss"]
  }; }
  static get styleUrls() { return {
    "$": ["c-button.css"]
  }; }
  static get properties() { return {
    "accessible": {
      "type": "boolean",
      "mutable": true,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": ""
      },
      "attribute": "accessible",
      "reflect": true,
      "defaultValue": "true"
    },
    "version": {
      "type": "string",
      "mutable": true,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": true,
      "docs": {
        "tags": [],
        "text": ""
      },
      "attribute": "version",
      "reflect": true,
      "defaultValue": "'3.0.0'"
    },
    "specificRole": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": true,
      "docs": {
        "tags": [],
        "text": ""
      },
      "attribute": "specific-role",
      "reflect": false
    },
    "href": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": ""
      },
      "attribute": "href",
      "reflect": true
    },
    "target": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "'_blank'",
        "resolved": "\"_blank\"",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": ""
      },
      "attribute": "target",
      "reflect": true
    },
    "primary": {
      "type": "boolean",
      "mutable": true,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": ""
      },
      "attribute": "primary",
      "reflect": true,
      "defaultValue": "false"
    },
    "minimal": {
      "type": "boolean",
      "mutable": true,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": ""
      },
      "attribute": "minimal",
      "reflect": true,
      "defaultValue": "false"
    },
    "block": {
      "type": "boolean",
      "mutable": true,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": ""
      },
      "attribute": "block",
      "reflect": true,
      "defaultValue": "false"
    },
    "rounded": {
      "type": "boolean",
      "mutable": true,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": ""
      },
      "attribute": "rounded",
      "reflect": true,
      "defaultValue": "false"
    },
    "alternate": {
      "type": "boolean",
      "mutable": true,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": ""
      },
      "attribute": "alternate",
      "reflect": true,
      "defaultValue": "false"
    },
    "attached": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "'left' | 'right' | 'top' | 'bottom' | 'horizontal' | 'vertical'",
        "resolved": "\"bottom\" | \"horizontal\" | \"left\" | \"right\" | \"top\" | \"vertical\"",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": ""
      },
      "attribute": "attached",
      "reflect": true
    },
    "disabled": {
      "type": "boolean",
      "mutable": true,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": ""
      },
      "attribute": "disabled",
      "reflect": true,
      "defaultValue": "false"
    },
    "active": {
      "type": "boolean",
      "mutable": true,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": ""
      },
      "attribute": "active",
      "reflect": true,
      "defaultValue": "false"
    },
    "centeredContent": {
      "type": "boolean",
      "mutable": true,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": ""
      },
      "attribute": "centered-content",
      "reflect": true,
      "defaultValue": "false"
    },
    "alert": {
      "type": "boolean",
      "mutable": true,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": ""
      },
      "attribute": "alert",
      "reflect": true,
      "defaultValue": "false"
    },
    "error": {
      "type": "boolean",
      "mutable": true,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": ""
      },
      "attribute": "error",
      "reflect": true,
      "defaultValue": "false"
    },
    "keyboardEventsEnabled": {
      "type": "boolean",
      "mutable": true,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": ""
      },
      "attribute": "keyboard-events-enabled",
      "reflect": true,
      "defaultValue": "true"
    },
    "ariaLabel": {
      "type": "string",
      "mutable": true,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": ""
      },
      "attribute": "aria-label",
      "reflect": true
    },
    "large": {
      "type": "boolean",
      "mutable": true,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": ""
      },
      "attribute": "large",
      "reflect": true,
      "defaultValue": "false"
    },
    "small": {
      "type": "boolean",
      "mutable": true,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": ""
      },
      "attribute": "small",
      "reflect": true,
      "defaultValue": "false"
    },
    "autoHeight": {
      "type": "boolean",
      "mutable": true,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": true,
      "docs": {
        "tags": [],
        "text": ""
      },
      "attribute": "auto-height",
      "reflect": true,
      "defaultValue": "false"
    },
    "focusable": {
      "type": "boolean",
      "mutable": true,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": ""
      },
      "attribute": "focusable",
      "reflect": false,
      "defaultValue": "true"
    },
    "readonly": {
      "type": "boolean",
      "mutable": true,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": ""
      },
      "attribute": "readonly",
      "reflect": true,
      "defaultValue": "false"
    }
  }; }
  static get states() { return {
    "role": {},
    "whatIntent": {}
  }; }
  static get events() { return [{
      "method": "buttonClicked",
      "name": "buttonClicked",
      "bubbles": true,
      "cancelable": true,
      "composed": true,
      "docs": {
        "tags": [],
        "text": ""
      },
      "complexType": {
        "original": "MouseEvent",
        "resolved": "MouseEvent",
        "references": {
          "MouseEvent": {
            "location": "global"
          }
        }
      }
    }, {
      "method": "buttonKeydown",
      "name": "buttonKeydown",
      "bubbles": true,
      "cancelable": true,
      "composed": true,
      "docs": {
        "tags": [],
        "text": ""
      },
      "complexType": {
        "original": "KeyboardEvent",
        "resolved": "KeyboardEvent",
        "references": {
          "KeyboardEvent": {
            "location": "global"
          }
        }
      }
    }]; }
  static get elementRef() { return "el"; }
  static get listeners() { return [{
      "name": "click",
      "method": "handleClick",
      "target": undefined,
      "capture": false,
      "passive": false
    }, {
      "name": "blur",
      "method": "handleBlur",
      "target": undefined,
      "capture": false,
      "passive": false
    }, {
      "name": "focus",
      "method": "handleFocus",
      "target": undefined,
      "capture": false,
      "passive": false
    }, {
      "name": "keydown",
      "method": "handleKeydown",
      "target": undefined,
      "capture": false,
      "passive": false
    }]; }
}
